"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { TimeLeft } from "@/helpers/waves/time.utils";
import { calculateTimeLeft } from "@/helpers/waves/time.utils";
import { useDecisionPoints } from "@/hooks/waves/useDecisionPoints";
import { AnimatePresence } from "framer-motion";
import { TimelineToggleHeader } from "./time/TimelineToggleHeader";
import { ExpandedTimelineContent } from "./time/ExpandedTimelineContent";
import { CompactDroppingPhaseCard } from "./time/CompactDroppingPhaseCard";
import { CompactVotingPhaseCard } from "./time/CompactVotingPhaseCard";
import { useWave } from "@/hooks/useWave";
import { Time } from "@/helpers/time";
interface WaveLeaderboardTimeProps {
  readonly wave: ApiWave;
}
const AUTO_EXPAND_LIMIT = 5;
const EMPTY_TIME_LEFT: TimeLeft = {
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0
};
export const WaveLeaderboardTime: React.FC<WaveLeaderboardTimeProps> = t0 => {
  const $ = _c(49);
  const {
    wave
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      initialPastWindow: 4,
      initialFutureWindow: 20
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    allDecisions,
    hasMorePast,
    hasMoreFuture,
    loadMorePast,
    loadMoreFuture,
    remainingPastCount,
    remainingFutureCount
  } = useDecisionPoints(wave, t1);
  const {
    decisions: t2,
    isCurationWave,
    pauses: t3
  } = useWave(wave);
  const {
    multiDecision
  } = t2;
  const {
    showPause,
    filterDecisionsDuringPauses
  } = t3;
  const [isDecisionDetailsOpen, setIsDecisionDetailsOpen] = useState(false);
  const [currentMillis, setCurrentMillis] = useState(_temp);
  const autoExpandFutureAttemptsRef = useRef(0);
  const [timelineFocus, setTimelineFocus] = useState(null);
  let t4;
  if ($[1] !== allDecisions || $[2] !== filterDecisionsDuringPauses) {
    const decisionsAsApiFormat = allDecisions.map(_temp2);
    const filtered = filterDecisionsDuringPauses(decisionsAsApiFormat);
    t4 = new Set(filtered.map(_temp3));
    $[1] = allDecisions;
    $[2] = filterDecisionsDuringPauses;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const allowedDecisionTimes = t4;
  let t5;
  if ($[4] !== allDecisions || $[5] !== allowedDecisionTimes) {
    let t6;
    if ($[7] !== allowedDecisionTimes) {
      t6 = decision_0 => allowedDecisionTimes.has(decision_0.timestamp);
      $[7] = allowedDecisionTimes;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    t5 = allDecisions.filter(t6);
    $[4] = allDecisions;
    $[5] = allowedDecisionTimes;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const filteredDecisions = t5;
  let t6;
  if ($[9] !== currentMillis || $[10] !== filteredDecisions) {
    t6 = filteredDecisions.find(decision_1 => decision_1.timestamp > currentMillis)?.timestamp ?? null;
    $[9] = currentMillis;
    $[10] = filteredDecisions;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const nextDecisionTime = t6;
  let t7;
  let t8;
  if ($[12] !== nextDecisionTime) {
    t7 = () => {
      if (typeof nextDecisionTime !== "number") {
        return;
      }
      const intervalId = globalThis.setInterval(() => {
        setCurrentMillis(Time.currentMillis());
      }, 1000);
      return () => {
        clearInterval(intervalId);
      };
    };
    t8 = [nextDecisionTime];
    $[12] = nextDecisionTime;
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[15] !== hasMoreFuture || $[16] !== loadMoreFuture || $[17] !== nextDecisionTime) {
    t9 = () => {
      if (nextDecisionTime !== null) {
        autoExpandFutureAttemptsRef.current = 0;
        return;
      }
      if (!hasMoreFuture) {
        autoExpandFutureAttemptsRef.current = 0;
        return;
      }
      if (autoExpandFutureAttemptsRef.current >= AUTO_EXPAND_LIMIT) {
        return;
      }
      let timeoutId = null;
      let isCancelled = false;
      const tick = () => {
        if (isCancelled) {
          return;
        }
        if (autoExpandFutureAttemptsRef.current >= AUTO_EXPAND_LIMIT) {
          return;
        }
        autoExpandFutureAttemptsRef.current = autoExpandFutureAttemptsRef.current + 1;
        loadMoreFuture();
        timeoutId = globalThis.setTimeout(tick, 50);
      };
      timeoutId = globalThis.setTimeout(tick, 50);
      return () => {
        isCancelled = true;
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
        }
      };
    };
    t10 = [nextDecisionTime, hasMoreFuture, loadMoreFuture];
    $[15] = hasMoreFuture;
    $[16] = loadMoreFuture;
    $[17] = nextDecisionTime;
    $[18] = t10;
    $[19] = t9;
  } else {
    t10 = $[18];
    t9 = $[19];
  }
  useEffect(t9, t10);
  let t11;
  if ($[20] !== currentMillis || $[21] !== nextDecisionTime) {
    t11 = typeof nextDecisionTime === "number" ? calculateTimeLeft(nextDecisionTime, currentMillis) : EMPTY_TIME_LEFT;
    $[20] = currentMillis;
    $[21] = nextDecisionTime;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const displayedTimeLeft = t11;
  let t12;
  if ($[23] !== hasMorePast || $[24] !== loadMorePast) {
    t12 = () => {
      if (hasMorePast) {
        setTimelineFocus("start");
        loadMorePast();
      }
    };
    $[23] = hasMorePast;
    $[24] = loadMorePast;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const handleLoadMorePast = t12;
  let t13;
  if ($[26] !== hasMoreFuture || $[27] !== loadMoreFuture) {
    t13 = () => {
      if (hasMoreFuture) {
        setTimelineFocus("end");
        loadMoreFuture();
      }
    };
    $[26] = hasMoreFuture;
    $[27] = loadMoreFuture;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const handleLoadMoreFuture = t13;
  let t14;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      setTimelineFocus(null);
    };
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const onTimelineFocusHandled = t14;
  let t15;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = isOpen => {
      setIsDecisionDetailsOpen(isOpen);
      if (!isOpen) {
        setTimelineFocus(null);
      }
    };
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const handleDecisionDetailsOpenChange = t15;
  let t16;
  if ($[31] !== displayedTimeLeft || $[32] !== filteredDecisions || $[33] !== handleLoadMoreFuture || $[34] !== handleLoadMorePast || $[35] !== hasMoreFuture || $[36] !== hasMorePast || $[37] !== isCurationWave || $[38] !== isDecisionDetailsOpen || $[39] !== multiDecision || $[40] !== nextDecisionTime || $[41] !== remainingFutureCount || $[42] !== remainingPastCount || $[43] !== showPause || $[44] !== timelineFocus || $[45] !== wave) {
    t16 = multiDecision ? <div className="tw-mt-2 tw-overflow-hidden tw-rounded-lg tw-bg-iron-950 md:tw-mt-4">{(() => {
        const currentPause = showPause(nextDecisionTime);
        return <TimelineToggleHeader isOpen={isDecisionDetailsOpen} setIsOpen={handleDecisionDetailsOpenChange} nextDecisionTime={nextDecisionTime} timeLeft={displayedTimeLeft} isPaused={Boolean(currentPause)} currentPause={currentPause} />;
      })()}<AnimatePresence>{isDecisionDetailsOpen && <ExpandedTimelineContent decisions={filteredDecisions} nextDecisionTime={nextDecisionTime} onLoadMorePast={hasMorePast ? handleLoadMorePast : undefined} onLoadMoreFuture={hasMoreFuture ? handleLoadMoreFuture : undefined} hasMorePast={hasMorePast} hasMoreFuture={hasMoreFuture} remainingPastCount={remainingPastCount} remainingFutureCount={remainingFutureCount} focus={timelineFocus} onFocusHandled={onTimelineFocusHandled} />}</AnimatePresence></div> : !isCurationWave && <div className="tw-overflow-hidden tw-rounded-lg tw-bg-iron-950 tw-px-3 tw-py-2"><div className="tw-flex tw-items-center tw-gap-2"><CompactDroppingPhaseCard wave={wave} /><CompactVotingPhaseCard wave={wave} /></div></div>;
    $[31] = displayedTimeLeft;
    $[32] = filteredDecisions;
    $[33] = handleLoadMoreFuture;
    $[34] = handleLoadMorePast;
    $[35] = hasMoreFuture;
    $[36] = hasMorePast;
    $[37] = isCurationWave;
    $[38] = isDecisionDetailsOpen;
    $[39] = multiDecision;
    $[40] = nextDecisionTime;
    $[41] = remainingFutureCount;
    $[42] = remainingPastCount;
    $[43] = showPause;
    $[44] = timelineFocus;
    $[45] = wave;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== t16) {
    t17 = <div>{t16}</div>;
    $[47] = t16;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  return t17;
};
function _temp() {
  return Time.currentMillis();
}
function _temp2(decision) {
  return {
    decision_time: decision.timestamp
  };
}
function _temp3(filteredDecision) {
  return filteredDecision.decision_time;
}